/**
 * DebugController - Debug panel and settings management
 */

import { DEBUG_SETTINGS_STORAGE_KEY } from '../constants/index.js';

// Module state (injected via init)
let debugConfig = null;
let portConnectionRules = null;
let renderAllCables = null;
let showToast = null;

/**
 * Initialize the debug controller with dependencies
 * @param {Object} deps - Dependencies
 * @param {Object} deps.debugConfig - Debug config object (cableStraightDist, cableStyle)
 * @param {Object} deps.portConnectionRules - Port connection rules object
 * @param {Function} deps.renderAllCables - Function to re-render all cables
 * @param {Function} deps.showToast - Function to show toast messages
 */
export function initDebugController(deps) {
    debugConfig = deps.debugConfig;
    portConnectionRules = deps.portConnectionRules;
    renderAllCables = deps.renderAllCables;
    showToast = deps.showToast;
}

/**
 * Get the current debug config (for external access)
 */
export function getDebugConfig() {
    return debugConfig;
}

/**
 * Toggle the debug panel visibility
 */
export function toggleDebugPanel() {
    const panel = document.getElementById('debug-panel');
    panel.classList.toggle('active');
    if (panel.classList.contains('active')) {
        renderPortRulesTable();
    }
}

/**
 * Update the cable straight distance setting
 * @param {string|number} value - New straight distance value
 */
export function updateDebugStraightDist(value) {
    debugConfig.cableStraightDist = parseInt(value);
    document.getElementById('debug-straight-dist-value').textContent = value;
    renderAllCables();
    saveDebugSettings();
}

/**
 * Update the cable style setting
 * @param {string} value - New cable style ('curvy' | 'orthogonal' | 'straight')
 */
export function updateDebugCableStyle(value) {
    debugConfig.cableStyle = value;
    renderAllCables();
    saveDebugSettings();
}

/**
 * Render the port rules table in the debug panel
 */
export function renderPortRulesTable() {
    const tbody = document.getElementById('port-rules-body');
    if (!tbody) return;
    
    tbody.innerHTML = '';
    
    // Built-in port types that cannot be deleted
    const builtInTypes = ['audio', 'midi', 'midi-rtp', 'midi-usb-host', 'midi-usb-device', 'cv', 'gate', '_default'];
    
    for (const [portType, rules] of Object.entries(portConnectionRules)) {
        const tr = document.createElement('tr');
        const isBuiltIn = builtInTypes.includes(portType);
        const displayName = portType === '_default' ? '(default)' : portType;
        
        tr.innerHTML = `
            <td>${displayName}</td>
            <td><input type="checkbox" ${rules.fanOut ? 'checked' : ''} onchange="updatePortRule('${portType}', 'fanOut', this.checked)"></td>
            <td><input type="checkbox" ${rules.fanIn ? 'checked' : ''} onchange="updatePortRule('${portType}', 'fanIn', this.checked)"></td>
            <td><button class="delete-btn" onclick="deletePortType('${portType}')" ${isBuiltIn ? 'disabled title="Built-in type"' : 'title="Delete"'}>×</button></td>
        `;
        tbody.appendChild(tr);
    }
}

/**
 * Update a port rule
 * @param {string} portType - The port type to update
 * @param {string} rule - The rule to update ('fanOut' or 'fanIn')
 * @param {boolean} value - The new value
 */
export function updatePortRule(portType, rule, value) {
    if (portConnectionRules[portType]) {
        portConnectionRules[portType][rule] = value;
        saveDebugSettings();
        showToast(`Updated ${portType} ${rule}: ${value}`);
    }
}

/**
 * Delete a custom port type
 * @param {string} portType - The port type to delete
 */
export function deletePortType(portType) {
    const builtInTypes = ['audio', 'midi', 'midi-rtp', 'midi-usb-host', 'midi-usb-device', 'cv', 'gate', '_default'];
    if (builtInTypes.includes(portType)) {
        showToast('Cannot delete built-in port type', true);
        return;
    }
    
    delete portConnectionRules[portType];
    renderPortRulesTable();
    saveDebugSettings();
    showToast(`Deleted port type: ${portType}`);
}

/**
 * Export all debug settings as JSON
 */
export function exportDebugSettings() {
    const settings = {
        portRules: portConnectionRules,
        cableSettings: {
            style: debugConfig.cableStyle,
            straightDist: debugConfig.cableStraightDist
        }
    };
    
    const json = JSON.stringify(settings, null, 2);
    const textarea = document.getElementById('export-textarea');
    const hint = document.getElementById('copy-hint');
    
    textarea.value = json;
    textarea.classList.add('active');
    
    // Try to copy to clipboard
    if (navigator.clipboard) {
        navigator.clipboard.writeText(json).then(() => {
            hint.classList.add('active');
            setTimeout(() => hint.classList.remove('active'), 2000);
        }).catch(() => {
            // Clipboard failed, user can copy manually
        });
    }
    
    // Select the text for easy manual copy
    textarea.select();
}

/**
 * Save debug settings to localStorage
 */
export function saveDebugSettings() {
    const settings = {
        portRules: portConnectionRules,
        cableSettings: {
            style: debugConfig.cableStyle,
            straightDist: debugConfig.cableStraightDist
        }
    };
    try {
        localStorage.setItem(DEBUG_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
    } catch (e) {
        console.warn('Failed to save debug settings:', e);
    }
}

/**
 * Load debug settings from localStorage
 * @param {Function} setPortConnectionRules - Function to update port connection rules
 * @returns {boolean} Whether settings were loaded
 */
export function loadDebugSettings(setPortConnectionRules) {
    try {
        const saved = localStorage.getItem(DEBUG_SETTINGS_STORAGE_KEY);
        if (saved) {
            const settings = JSON.parse(saved);
            
            // Restore port rules (merge with defaults to ensure built-ins exist)
            if (settings.portRules) {
                const defaultRules = {
                    'midi':           { fanOut: true,  fanIn: true  },
                    'midi-rtp':       { fanOut: true,  fanIn: true  },
                    'midi-usb-host':  { fanOut: true,  fanIn: false },
                    'midi-usb-device':{ fanOut: true,  fanIn: false },
                    'audio':          { fanOut: false, fanIn: false },
                    'cv':             { fanOut: true,  fanIn: false },
                    'gate':           { fanOut: true,  fanIn: false },
                    '_default':       { fanOut: false, fanIn: false }
                };
                // Merge saved rules with defaults (saved takes precedence)
                const mergedRules = { ...defaultRules, ...settings.portRules };
                if (setPortConnectionRules) {
                    setPortConnectionRules(mergedRules);
                }
                // Update module reference
                portConnectionRules = mergedRules;
            }
            
            // Restore cable settings
            if (settings.cableSettings) {
                if (settings.cableSettings.style) {
                    debugConfig.cableStyle = settings.cableSettings.style;
                    const select = document.getElementById('debug-cable-style');
                    if (select) select.value = debugConfig.cableStyle;
                }
                if (typeof settings.cableSettings.straightDist === 'number') {
                    debugConfig.cableStraightDist = settings.cableSettings.straightDist;
                    const slider = document.getElementById('debug-straight-dist');
                    const value = document.getElementById('debug-straight-dist-value');
                    if (slider) slider.value = debugConfig.cableStraightDist;
                    if (value) value.textContent = debugConfig.cableStraightDist;
                }
            }
            
            return true;
        }
    } catch (e) {
        console.warn('Failed to load debug settings:', e);
    }
    return false;
}
